import { useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { Checkbox } from "@mui/material";
import Input from "components/Input";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import SearchByNip from "components/SearchByNip";
import workspaceService from "api/workspaceService";
import { IBillingInfo } from "dto/IBillingInfo";
import { IGUSCompany } from "dto/IGUSCompany";
import { ENotificationType, notification } from "utils/notification";

interface Props {
  workspaceId?: string;
  info?: IBillingInfo;
  onBack: () => void;
  onSaved: () => void;
}

const EditBuyerPanel = ({ workspaceId, info, onBack, onSaved }: Props) => {
  const [companyName, setCompanyName] = useState(info?.CompanyName ?? "");
  const [nip, setNip] = useState(info?.NIP ?? "");
  const [street, setStreet] = useState(info?.Street ?? "");
  const [zipCode, setZipCode] = useState(info?.ZipCode ?? "");
  const [city, setCity] = useState(info?.City ?? "");
  const [contactName, setContactName] = useState(info?.ContactName ?? "");
  const [email, setEmail] = useState(info?.Email ?? "");
  const [phone, setPhone] = useState(info?.Phone ?? "");
  const [issueInvoice, setIssueInvoice] = useState(info?.IssueInvoice ?? true);

  const { mutate, isLoading } = workspaceService.useSaveBillingInfo();

  // SearchByNip auto-odpytuje GUS dla NIP-u początkowego przy montażu — uzupełniamy i powiadamiamy
  // dopiero gdy użytkownik sam zmieni NIP (inaczej notyfikacja pojawiałaby się przy otwarciu panelu).
  const nipTouched = useRef(false);

  // Po wpisaniu poprawnego NIP SearchByNip pobiera dane z GUS (BE) i uzupełnia formularz.
  const handleCompanyData = (company: IGUSCompany) => {
    if (!nipTouched.current) return;
    if (!company?.Name) return;
    setCompanyName(company.Name);
    if (company.ZipCode) setZipCode(company.ZipCode);
    if (company.Place) setCity(company.Place);

    const houseLine = company.HouseNumber
      ? `${company.HouseNumber}${company.LocalNumber ? `/${company.LocalNumber}` : ""}`
      : "";
    const streetLine = [company.Street, houseLine].filter(Boolean).join(" ");
    if (streetLine) setStreet(streetLine);

    notification({
      type: ENotificationType.INFO,
      title: "Dane firmy",
      text: "Uzupełniono dane na podstawie NIP (GUS). Sprawdź i w razie potrzeby popraw.",
    });
  };

  const handleSave = () => {
    mutate(
      {
        WorkspaceId: workspaceId?.length ? workspaceId : null,
        CompanyName: companyName,
        NIP: nip,
        Street: street,
        ZipCode: zipCode,
        City: city,
        ContactName: contactName,
        Email: email,
        Phone: phone,
        IssueInvoice: issueInvoice,
      },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Dane rozliczeniowe",
            text: "Zapisano dane nabywcy.",
          });
          onSaved();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Dane rozliczeniowe",
            text: "Nie udało się zapisać danych.",
          }),
      }
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.breadcrumb}>
        <FontAwesomeIcon
          icon={faArrowLeft}
          className={styles.back}
          onClick={onBack}
        />
        <span className={styles.muted}>Płatności</span>
        <FontAwesomeIcon icon={faChevronLeft} className={styles.sep} />
        <span>Informacje rozliczeniowe</span>
      </div>

      <div className={styles.hint}>
        Dane nabywcy są wykorzystywane do wystawiania faktur za subskrypcję.
      </div>

      <div className={styles.grid}>
        <div className={styles.full}>
          <Input label="Nazwa" value={companyName} onChange={setCompanyName} />
        </div>
        <SearchByNip
          defaultValue={nip}
          onChange={(t) => {
            nipTouched.current = true;
            setNip(t);
          }}
          onDataChange={handleCompanyData}
        />
        <Input label="Adres" value={street} onChange={setStreet} />
        <Input label="Kod pocztowy" value={zipCode} onChange={setZipCode} isCode />
        <Input label="Miasto" value={city} onChange={setCity} />
        <div className={styles.full}>
          <Input
            label="Imię i nazwisko"
            value={contactName}
            onChange={setContactName}
          />
        </div>
        <Input label="Email" value={email} onChange={setEmail} isEmail />
        <Input label="Telefon" value={phone} onChange={setPhone} isPhoneNumber />
      </div>

      <div className={styles.issue}>
        <Checkbox
          size="small"
          color="primary"
          checked={issueInvoice}
          onChange={(e) => setIssueInvoice(e.target.checked)}
          sx={{ padding: 0 }}
        />
        <span>Wystaw fakturę</span>
      </div>

      <div className={styles.actions}>
        <HollowButton text="Anuluj" onClick={onBack} />
        <Button text="Zapisz" onClick={handleSave} loading={isLoading} />
      </div>
    </div>
  );
};

export default EditBuyerPanel;
